import { Attachment } from '../models/submissions/attachment';
import { Asset } from '../models/asset/dto/asset';
import { AssetSize } from '../models/enum/dto/asset-size.enum';

/** S3 key prefix used for the write-only upload location. */
const INPUT_PATH_SEGMENT = '/Input/';

export class AttachmentUtils {
  /**
   * The presigned URL that can actually be read (GET) - i.e. the Output link.
   *
   * When an attachment is first created the API hands back an *Input* presigned URL, which is
   * signed for the upload (PUT) only; GETting it returns a 403. The readable Output link only
   * appears once the attachment is re-fetched after processing. Returns null until then, so
   * callers can hold off on previewing/downloading rather than firing a request that must fail.
   */
  public static getReadableUrl(attachment: Attachment | null | undefined): string | null {
    if (!attachment) {
      return null;
    }
    const url = attachment.links?.find(
      link => link.presignedUrl && !link.presignedUrl.includes(INPUT_PATH_SEGMENT)
    )?.presignedUrl;
    return url ?? null;
  }

  public static getReadableAssetUrl(asset: Asset | null | undefined, sizes: AssetSize[]): string | null {
    for (const size of sizes) {
      const url = asset?.links?.find(link => link.size === size)?.presignedUrl;
      if (url && !url.includes(INPUT_PATH_SEGMENT)) {
        return url;
      }
    }
    return null;
  }
}

/**
 * Every upload refuses `/`, `(` and `)` in a file name, and some records also refuse a name they already hold (ignoring
 * case), so a picked file's name is cleaned up before its attachment is created.
 */
export function toUploadableFileName(fileName: string, existingFileNames: string[] = []): string {
  const extensionIndex = fileName.lastIndexOf('.');
  const hasExtension = extensionIndex > 0;
  const extension = hasExtension ? fileName.slice(extensionIndex) : '';
  const name = hasExtension ? fileName.slice(0, extensionIndex) : fileName;
  const cleanedName = /[/()]/.test(name)
    ? name.replace(/[\s-]*[/()][\s/()-]*/g, '-').replace(/^-+|-+$/g, '') || '-'
    : name;

  const takenNames = new Set(existingFileNames.map(existing => existing.toLowerCase()));
  let candidate = `${cleanedName}${extension}`;
  for (let copy = 2; takenNames.has(candidate.toLowerCase()); copy++) {
    candidate = `${cleanedName}-${copy}${extension}`;
  }
  return candidate;
}
